import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import { Icon } from '@/components/icon';
import { ApiError } from '@/api/client';
import {
  getMfaStatus,
  beginMfaEnroll,
  confirmMfaEnroll,
  disableMfa,
  type MfaStatus,
} from '@/api/mfa';

/**
 * Settings › Security — two-factor authentication (TOTP).
 *
 * Three states driven by the real /auth/mfa/status:
 *   • disabled → "Enable" starts enrollment (shows secret + otpauth URI for
 *     manual entry into any authenticator app) → confirm with a 6-digit code
 *     ACTIVATES it. Nothing gates login until that confirm succeeds.
 *   • enabled  → shows an "Active" badge + a code-gated "Disable".
 *
 * No QR-image dependency: the otpauth:// URI is shown as copyable text plus the
 * raw base32 secret, which every authenticator app accepts for manual entry.
 */
export function MfaCard(): JSX.Element {
  const { t } = useTranslation(['settings']);
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [disableMode, setDisableMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      setStatus(await getMfaStatus(signal));
    } catch {
      // Unauthenticated or transient — leave status null (card shows loading).
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const onEnable = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const r = await beginMfaEnroll();
      setSecret(r.secret);
      setOtpauthUri(r.otpauth_uri);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'mfa_already_enabled'
          ? t('security.error_already_enabled', { defaultValue: 'MFA is already enabled.' })
          : t('security.error_generic', { defaultValue: 'Something went wrong. Try again.' }),
      );
    } finally {
      setBusy(false);
    }
  }, [t]);

  const onConfirm = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await confirmMfaEnroll(code.trim());
      setSecret(null);
      setOtpauthUri(null);
      setCode('');
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'invalid_mfa_code'
          ? t('security.error_invalid_code', { defaultValue: 'Invalid code. Try the current one.' })
          : t('security.error_generic', { defaultValue: 'Something went wrong. Try again.' }),
      );
    } finally {
      setBusy(false);
    }
  }, [code, load, t]);

  const onDisable = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await disableMfa(code.trim());
      setDisableMode(false);
      setCode('');
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'invalid_mfa_code'
          ? t('security.error_invalid_code', { defaultValue: 'Invalid code. Try the current one.' })
          : t('security.error_generic', { defaultValue: 'Something went wrong. Try again.' }),
      );
    } finally {
      setBusy(false);
    }
  }, [code, load, t]);

  const codeValid = /^\d{6}$/.test(code.trim());
  const enrolling = secret !== null;

  return (
    <Card data-testid="settings-security-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="shield" size={18} aria-hidden />
          {t('security.title', { defaultValue: 'Security' })}
        </CardTitle>
        <CardDescription>
          {t('security.description', {
            defaultValue: 'Two-factor authentication (TOTP) for your account.',
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error && (
          <Alert variant="danger" data-testid="settings-security-error">
            {error}
          </Alert>
        )}

        {status === null ? (
          <p className="text-sm text-muted-foreground">
            {t('security.loading', { defaultValue: 'Loading…' })}
          </p>
        ) : status.enabled ? (
          // ---- ACTIVE ----
          <div className="flex flex-col gap-3" data-testid="settings-security-enabled">
            <Badge variant="success" icon="check-circle">
              {t('security.active', { defaultValue: 'Two-factor is active' })}
            </Badge>
            {disableMode ? (
              <div className="flex flex-col gap-2">
                <label htmlFor="mfa-disable-code" className="text-xs font-medium text-muted-foreground">
                  {t('security.confirm_code_label', { defaultValue: 'Enter a current code to confirm' })}
                </label>
                <Input
                  id="mfa-disable-code"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  data-testid="settings-security-disable-code"
                />
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    disabled={!codeValid || busy}
                    onClick={() => void onDisable()}
                    data-testid="settings-security-disable-confirm"
                  >
                    {t('security.disable_confirm', { defaultValue: 'Disable' })}
                  </Button>
                  <Button variant="outline" onClick={() => { setDisableMode(false); setCode(''); }}>
                    {t('security.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => { setDisableMode(true); setError(null); }}
                data-testid="settings-security-disable"
              >
                {t('security.disable', { defaultValue: 'Disable two-factor' })}
              </Button>
            )}
          </div>
        ) : enrolling ? (
          // ---- ENROLLING (secret shown, awaiting confirm) ----
          <div className="flex flex-col gap-3" data-testid="settings-security-enrolling">
            <p className="text-sm text-muted-foreground">
              {t('security.enroll_hint', {
                defaultValue: 'Add this secret to your authenticator app, then enter a code to confirm.',
              })}
            </p>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('security.secret_label', { defaultValue: 'Secret' })}
              </span>
              <code
                className="select-all break-all rounded-md border border-border bg-muted px-2 py-1 text-sm"
                data-testid="settings-security-secret"
              >
                {secret}
              </code>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('security.uri_label', { defaultValue: 'Or import this URI' })}
              </span>
              <code className="select-all break-all rounded-md border border-border bg-muted px-2 py-1 text-xs">
                {otpauthUri}
              </code>
            </div>
            <label htmlFor="mfa-confirm-code" className="text-xs font-medium text-muted-foreground">
              {t('security.confirm_code_label', { defaultValue: 'Enter a current code to confirm' })}
            </label>
            <Input
              id="mfa-confirm-code"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              data-testid="settings-security-confirm-code"
            />
            <div className="flex gap-2">
              <Button
                disabled={!codeValid || busy}
                onClick={() => void onConfirm()}
                data-testid="settings-security-confirm"
              >
                {t('security.confirm', { defaultValue: 'Confirm & enable' })}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setSecret(null); setOtpauthUri(null); setCode(''); }}
              >
                {t('security.cancel', { defaultValue: 'Cancel' })}
              </Button>
            </div>
          </div>
        ) : (
          // ---- DISABLED ----
          <div className="flex flex-col gap-3" data-testid="settings-security-disabled">
            <Badge variant="neutral">
              {t('security.inactive', { defaultValue: 'Two-factor is off' })}
            </Badge>
            <Button
              disabled={busy}
              onClick={() => void onEnable()}
              data-testid="settings-security-enable"
            >
              {t('security.enable', { defaultValue: 'Enable two-factor' })}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
